import { print } from "graphql";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { TOOLKIT_PRODUCT_ID } from "../config/toolkit-config";
import { LanguageContext } from "../contexts/app-context";
import { translationsQuery } from "../queries/queries";
import { Maybe, Texts_Text } from "../generated/generated-operations";
import { requestGraphql } from "../queries/graphql-client";
import { TranslationProduct, TranslationTextRef } from "../texts/texts";
import { Language } from "../types/types";

type TranslationResponse = {
  readonly products?: ReadonlyArray<{
    readonly id?: string | null;
    readonly key?: string | null;
    readonly name?: string | null;
    readonly modules?: {
      readonly texts?: {
        readonly text?: ReadonlyArray<Texts_Text>;
      };
    };
  }>;
};

type TranslationData = {
  readonly productName: string;
  readonly currentProductKey: string;
  readonly translationsByProduct: ReadonlyMap<string, ReadonlyMap<string, string>>;
};

type TranslationInput = Maybe<string> | TranslationTextRef;
type TranslationSelector = TranslationProduct;

function normalizeProductKey(value?: string | null): string {
  return (value ?? "").trim();
}

function toPlaceholderProductName(productName?: string | null): string {
  if (!productName || productName.trim().length === 0) {
    return "productname";
  }

  return productName.trim().toLowerCase().replace(/\s+/g, "_");
}

function placeholder(productName: string, key: string): string {
  return `{${productName}.${key}}`;
}

function toTranslationMap(values?: ReadonlyArray<Texts_Text>): ReadonlyMap<string, string> {
  const result = new Map<string, string>();
  for (const value of values ?? []) {
    if (value.name && value.text && value.text.trim().length > 0) {
      result.set(value.name, value.text);
    }
  }

  return result;
}

async function fetchTranslations(productId: string, language: Language): Promise<TranslationData> {
  const data = await requestGraphql<TranslationResponse, { language: Language }>(print(translationsQuery), {
    language,
  });

  const products = data.products;
  if (!Array.isArray(products)) {
    throw new Error("Error.");
  }

  const currentProduct = products.find((product) => product.id === productId);
  const translationsByProduct = new Map<string, ReadonlyMap<string, string>>();
  for (const product of products) {
    const productKey = normalizeProductKey(product.key ?? product.name);
    if (!productKey) {
      continue;
    }

    const textEntries = product.modules?.texts?.text;
    if (textEntries && !Array.isArray(textEntries)) {
      throw new Error("Error.");
    }

    translationsByProduct.set(productKey, toTranslationMap(textEntries));
  }

  const currentProductKey = normalizeProductKey(currentProduct?.key ?? currentProduct?.name);
  const placeholderProductName = toPlaceholderProductName(currentProduct?.name);

  return {
    productName: placeholderProductName,
    currentProductKey,
    translationsByProduct,
  };
}

function parseInput(input?: TranslationInput): { readonly key: string; readonly product: TranslationSelector } | null {
  if (!input) {
    return null;
  }

  if (typeof input === "string") {
    return { key: input, product: "Root" };
  }

  return { key: input.key, product: input.product ?? "Root" };
}

function productPlaceholderName(product: TranslationSelector, data: TranslationData): string {
  const normalized = normalizeProductKey(product);

  if (normalized === "current") {
    return data.currentProductKey || data.productName;
  }

  return normalized || data.productName;
}

function resolveTranslation(key: string, product: TranslationSelector, data: TranslationData): string {
  const selectedProductKey =
    normalizeProductKey(product) === "current" ? data.currentProductKey : normalizeProductKey(product);

  const selectedTranslations = data.translationsByProduct.get(selectedProductKey);
  return selectedTranslations?.get(key) ?? placeholder(productPlaceholderName(product, data), key);
}

export function useTranslation(): (key?: TranslationInput, product?: TranslationSelector) => string {
  const language = useContext(LanguageContext);

  const query = useQuery({
    queryKey: ["translations", language, TOOLKIT_PRODUCT_ID],
    queryFn: () => fetchTranslations(TOOLKIT_PRODUCT_ID, language),
    refetchOnWindowFocus: true,
  });

  function translateFn(input?: TranslationInput, product?: TranslationSelector): string {
    const parsedInput = parseInput(input);
    if (!parsedInput) {
      return "";
    }

    const selectedProduct = product ?? parsedInput.product;

    if (query.isError) {
      return placeholder(normalizeProductKey(selectedProduct) || "root", parsedInput.key);
    }

    const data = query.data ?? {
      productName: "productname",
      currentProductKey: "",
      translationsByProduct: new Map<string, ReadonlyMap<string, string>>(),
    };

    return resolveTranslation(parsedInput.key, selectedProduct, data);
  }

  return translateFn;
}
